(function (root) {
  const SITE_ID = 'yKaaV9';
  const REVISION = '2026-07-15';
  const METRIC = 'Email Open Tracking Choice Submitted';
  const FAQ_ANSWER_ID = 'o46_Nyw7Qo2C3ob9SNqQ3w';
  const FAQ_ANSWER = `Email-Open-Tracking---id--${FAQ_ANSWER_ID}`;
  const countryCode = value => /^[A-Z]{2}$/.test(String(value || '').toUpperCase()) ? String(value).toUpperCase() : null;
  const inScope = country => country === 'FR' || country === 'IT';
  const isTrackingFaqAnswer = (country, answer) => inScope(country) && typeof answer === 'string' && answer.split('---id--')[1] === FAQ_ANSWER_ID;
  const delay = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

  function localizedFaqUrl(value, localeRoot, storefrontUrl) {
    const storefront = new URL(storefrontUrl);
    const url = new URL(value, storefront);
    const sameStore = url.hostname.replace(/^www\./, '') === storefront.hostname.replace(/^www\./, '');
    if (sameStore && /^https?:$/.test(url.protocol)) {
      const pagePath = url.pathname.match(/^(?:\/[a-z]{2}(?:-[a-z]{2})?)?(\/pages\/.*)$/i);
      if (pagePath) url.pathname = `${localeRoot.replace(/\/$/, '')}${pagePath[1]}`;
    }
    return url.href;
  }

  async function detectCountry(request, fallback, pause = delay) {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await request();
        if (!response.ok) throw new Error('Location request failed');
        const data = await response.json();
        const country = countryCode(data.detected_values?.country?.handle);
        if (country) return { country, source: 'shopify_detection' };
      } catch (_) {}
      if (attempt === 0) await pause(300);
    }
    const country = countryCode(fallback());
    if (!country) throw new Error('Country unavailable');
    return { country, source: 'shopify_country' };
  }

  function eventPayload(snapshot) {
    const withdrawal = snapshot.action === 'withdraw';
    return {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: withdrawal ? 'Email Open Tracking Withdrawal Requested' : METRIC } } },
          profile: { data: { type: 'profile', attributes: { email: snapshot.email } } },
          time: snapshot.time,
          unique_id: snapshot.id,
          properties: {
            email: snapshot.email,
            country: snapshot.country,
            country_source: snapshot.source,
            consent: withdrawal ? 'UNSUBSCRIBED' : inScope(snapshot.country) ? (snapshot.checked ? 'SUBSCRIBED' : 'UNSUBSCRIBED') : 'NOT_APPLICABLE',
            consent_shown: withdrawal || inScope(snapshot.country),
            form_id: snapshot.formId,
            page_path: snapshot.path,
            copy_version: snapshot.copyVersion,
            submission_id: snapshot.id
          }
        }
      }
    };
  }

  async function sendEvent(snapshot, request, pause = delay) {
    const body = JSON.stringify(eventPayload(snapshot));
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await request(body);
        if (response.ok) return;
        if (response.status !== 429 && response.status < 500) break;
      } catch (_) {}
      if (attempt === 0) await pause(1000);
    }
    throw new Error('Consent event not accepted');
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { countryCode, inScope, isTrackingFaqAnswer, localizedFaqUrl, detectCountry, eventPayload, sendEvent };
    return;
  }

  const document = root.document;
  const configElement = document.getElementById('OpenTrackingConfig');
  if (!configElement || root.DoenOpenTracking) return;
  const config = JSON.parse(configElement.textContent);
  const previewCountry = config.allowPreview && countryCode(new URLSearchParams(root.location.search).get('open_tracking_preview'));
  const active = config.enabled || Boolean(previewCountry);
  const states = new Map();
  const eventPromises = new WeakMap();
  let location = null;
  let locationFailed = false;

  async function request(url, options = {}, timeout = 6000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  }

  const needsLocation = active || config.designMode || document.querySelector('[data-open-tracking-faq]');
  const ready = !needsLocation ? Promise.resolve() : (previewCountry
    ? Promise.resolve({ country: previewCountry, source: 'preview' })
    : detectCountry(
      () => request(`${config.root}browsing_context_suggestions.json?country[enabled]=true`, {}, 2000),
      () => root.Shopify?.country || config.fallbackCountry
    )).then(result => {
      location = result;
      states.forEach(refresh);
      setupFaq();
    }).catch(() => {
      locationFailed = true;
    });

  function status(state, message, retry = false) {
    if (!state.status?.isConnected) {
      state.status = document.getElementById('OpenTrackingStatus').content.firstElementChild.cloneNode(true);
      state.host.append(state.status);
      state.status.querySelector('[data-tracking-retry]').addEventListener('click', () => persist(state));
    }
    state.status.hidden = !message;
    state.status.querySelector('[data-tracking-message]').textContent = message;
    state.status.querySelector('[data-tracking-retry]').hidden = !retry;
  }

  function emailInput(state) {
    return Array.from(state.host.querySelectorAll('input[type="email"]')).find(input => input.getClientRects().length && !input.disabled);
  }

  function refresh(state) {
    if (!location || !inScope(location.country)) return;
    const input = emailInput(state);
    if (input && !state.control?.isConnected) {
      if (state.host.closest('.newsletter-signup')) {
        const form = input.closest('form');
        const background = form && root.getComputedStyle(form).backgroundImage;
        if (background && background !== 'none') state.host.style.setProperty('--open-tracking-paper', background);
      }
      state.control = document.getElementById('OpenTrackingControl').content.firstElementChild.cloneNode(true);
      const faqLink = state.control.querySelector('[data-tracking-faq-link]');
      faqLink.href = localizedFaqUrl(faqLink.getAttribute('href'), root.Shopify?.routes?.root || config.root, root.location.href);
      faqLink.hidden = !config.showFaqLink;
      const checkbox = state.control.querySelector('[data-tracking-choice]');
      checkbox.checked = state.checked;
      checkbox.addEventListener('change', () => { state.checked = checkbox.checked; });
      state.host.append(state.control);
    }
    if (state.control) {
      const hidden = !input || Boolean(state.pending);
      if (state.control.hidden !== hidden) state.control.hidden = hidden;
    }
  }

  function attach(host, formId) {
    if (states.has(host)) return states.get(host);
    const state = { host, formId, checked: false };
    states.set(host, state);
    state.observer = new MutationObserver(() => refresh(state));
    state.observer.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'disabled'] });
    const overlay = host.closest('.klaviyo-overlay');
    if (overlay) state.observer.observe(overlay, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    refresh(state);
    return state;
  }

  function scan() {
    states.forEach((state, host) => {
      if (!host.isConnected) {
        state.observer.disconnect();
        states.delete(host);
      }
    });
    const custom = document.getElementById('klaviyo-overlay__email-form');
    if (custom) {
      if (previewCountry) custom.closest('.klaviyo-overlay').classList.remove('hide');
      attach(custom, 'custom_flyup');
    }
    document.querySelectorAll('[data-open-tracking-form]').forEach(host => attach(host, host.dataset.openTrackingForm));
  }

  function snapshot(state, email) {
    return {
      ...location,
      email: email.trim(),
      checked: state.checked,
      id: crypto.randomUUID(),
      time: new Date().toISOString(),
      formId: state.formId,
      path: root.location.pathname,
      copyVersion: config.copyVersion
    };
  }

  function guard(event) {
    const state = Array.from(states.values()).find(item => item.host.contains(event.target));
    if (!state || event.target.closest('.doen-open-tracking, .doen-open-tracking-status')) return;
    if (event.type === 'click' && !event.target.closest('button, input[type="submit"]')) return;
    if (event.type === 'keydown' && event.key !== 'Enter') return;
    const input = emailInput(state);
    if (!input) return;
    if (previewCountry || !location || state.pending) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!state.pending) status(state, previewCountry ? config.previewMessage : locationFailed ? config.locationError : config.loading);
      return;
    }
    refresh(state);
    state.snapshot = snapshot(state, input.value);
    if (state.status) status(state, '');
  }

  async function persist(state) {
    if (!state.pending || state.saving) return;
    state.saving = true;
    status(state, '');
    try {
      await postEvent(state.pending);
      state.lastSaved = state.pending;
      state.pending = null;
      status(state, config.saved);
      state.resolve();
    } catch (_) {
      status(state, config.saveError, true);
    } finally {
      state.saving = false;
    }
  }

  function postEvent(captured) {
    return sendEvent(captured, body => request(`https://a.klaviyo.com/client/events/?company_id=${SITE_ID}`, {
      method: 'POST',
      headers: { revision: REVISION, 'Content-Type': 'application/vnd.api+json' },
      body,
      keepalive: true
    }));
  }

  function setupFaq() {
    document.documentElement.toggleAttribute('data-open-tracking-in-scope', inScope(location?.country));
    const url = new URL(root.location.href);
    const sections = document.querySelectorAll('[data-open-tracking-faq]');
    // Preserve existing theme and checkout links after moving the answer to Gladly.
    if (sections.length && inScope(location?.country) && !url.searchParams.has('a') && ['#open-tracking', '#open-tracking-withdraw'].includes(url.hash)) {
      url.searchParams.set('a', FAQ_ANSWER);
      if (url.hash === '#open-tracking') url.hash = '';
      root.location.replace(url.href);
      return;
    }
    const showWithdrawal = isTrackingFaqAnswer(location?.country, url.searchParams.get('a')) && Boolean(document.getElementById(`gladlyHC-answer-${FAQ_ANSWER_ID}`));
    sections.forEach(section => {
      section.hidden = !showWithdrawal;
    });
    if (showWithdrawal) setupWithdrawals();
  }

  const observedFaqs = new WeakSet();
  function observeFaq() {
    document.querySelectorAll('#gladly-help-center').forEach(widget => {
      if (observedFaqs.has(widget)) return;
      observedFaqs.add(widget);
      // Gladly replaces its answer DOM on navigation without reloading the page.
      new MutationObserver(setupFaq).observe(widget, { childList: true, subtree: true });
    });
    setupFaq();
  }

  function setupWithdrawals() {
    document.querySelectorAll('[data-tracking-withdrawal]').forEach(container => {
      if (container.dataset.initialized) return;
      container.dataset.initialized = 'true';
      const preview = Boolean(previewCountry || config.designMode);
      if (container.dataset.enabled !== 'true' && !preview) return;
      container.hidden = false;
      const form = container.querySelector('form');
      const input = form.querySelector('input[type="email"]');
      const button = form.querySelector('button');
      const message = form.querySelector('[role="status"]');
      button.disabled = false;
      input.disabled = false;
      let pending = null;
      let saving = false;
      let savedEmail = null;
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (container.closest('[data-open-tracking-faq]')?.hidden || !inScope(location?.country) || saving || !form.reportValidity()) return;
        if (preview) {
          message.textContent = container.dataset.preview;
          return;
        }
        const email = input.value.trim();
        if (savedEmail === email.toLowerCase()) {
          message.textContent = container.dataset.success;
          return;
        }
        if (!pending || pending.email.toLowerCase() !== email.toLowerCase()) {
          pending = {
            action: 'withdraw',
            email,
            country: location?.country || countryCode(root.Shopify?.country || config.fallbackCountry),
            source: location?.source || 'shopify_country',
            checked: false,
            id: crypto.randomUUID(),
            time: new Date().toISOString(),
            formId: 'faq_withdrawal',
            path: root.location.pathname,
            copyVersion: container.dataset.copyVersion
          };
        }
        saving = true;
        button.disabled = true;
        input.readOnly = true;
        form.setAttribute('aria-busy', 'true');
        message.textContent = container.dataset.saving;
        try {
          await postEvent(pending);
          savedEmail = email.toLowerCase();
          pending = null;
          message.textContent = container.dataset.success;
        } catch (_) {
          message.textContent = container.dataset.error;
        } finally {
          saving = false;
          button.disabled = false;
          input.readOnly = false;
          form.removeAttribute('aria-busy');
        }
      });
    });
  }

  function record(state, captured) {
    if (state.pending) return state.completion;
    state.pending = captured;
    state.completion = new Promise(resolve => { state.resolve = resolve; });
    refresh(state);
    persist(state);
    return state.completion;
  }

  function handleKlaviyoEvent(event) {
    if (!active || previewCountry) return Promise.resolve();
    if (eventPromises.has(event)) return eventPromises.get(event);
    const detail = event.detail || {};
    if (!['submit', 'stepSubmit'].includes(detail.type) || (detail.companyId && detail.companyId !== SITE_ID)) return Promise.resolve();
    const email = detail.metaData?.email || detail.metaData?.$email;
    const state = Array.from(states.values()).find(item => item.formId === detail.formId && item.snapshot?.email.toLowerCase() === email?.trim().toLowerCase());
    if (!state || !email) return Promise.resolve();
    if (state.lastSaved?.id === state.snapshot.id) return Promise.resolve();
    const completion = record(state, state.snapshot);
    eventPromises.set(event, completion);
    return completion;
  }

  root.DoenOpenTracking = {
    active,
    ready,
    handleKlaviyoEvent,
    prepare(form, email) {
      if (!active) return null;
      const state = states.get(form);
      if (previewCountry || !location || !state) throw new Error('Consent capture unavailable');
      return snapshot(state, email);
    },
    record(form, captured) {
      if (!active || !captured) return Promise.resolve();
      return record(states.get(form), captured);
    }
  };

  observeFaq();
  document.addEventListener('shopify:section:load', observeFaq);
  root.addEventListener('popstate', setupFaq);
  root.addEventListener('hashchange', setupFaq);
  if (!active) return;
  scan();
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', scan);
  ['click', 'submit', 'keydown'].forEach(type => document.addEventListener(type, guard, true));
  root.addEventListener('klaviyoForms', handleKlaviyoEvent, true);
})(globalThis);
